import React, { useEffect, useRef, useState, useCallback } from "react";
import { Reorder, motion, AnimatePresence } from "framer-motion";
import {
  FaPlus, FaTrash, FaSave, FaTimes, FaImage, FaVideo, FaGripVertical,
  FaPencilAlt, FaCloudUploadAlt, FaSpinner, FaLayerGroup, FaNewspaper,
  FaEye, FaEyeSlash, FaDesktop,
} from "react-icons/fa";
import {
  getContentBlocks, createContentBlock, updateContentBlock, deleteContentBlock,
  getContentArticles, createContentArticle, updateContentArticle, deleteContentArticle,
  reorderContent, uploadContentMedia,
} from "../../services/api";
import ExploreSection, { ExploreBlock } from "../layout/ExploreSection";
import ArticleCarousel, { CarouselArticle } from "../layout/ArticleCarousal";

import { useConfirm } from "../common/ConfirmDialog";
// ── Live-preview frame (a little browser chrome around the real component) ──────
const PreviewFrame: React.FC<{ children: React.ReactNode; note: string }> = ({ children, note }) => (
  <div className="mt-8">
    <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
      <FaDesktop /> Live preview — {note}
    </div>
    <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm bg-white">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 border-b border-gray-200">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-3 text-[11px] text-gray-400">buildmytravel.ae</span>
      </div>
      <div className="max-h-[75vh] overflow-y-auto bg-white">{children}</div>
    </div>
  </div>
);

const PreviewToggle: React.FC<{ on: boolean; onClick: () => void }> = ({ on, onClick }) => (
  <button onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition
      ${on ? "bg-primary/10 text-primary border-primary/30" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>
    <FaDesktop size={12} /> {on ? "Hide preview" : "Preview"}
  </button>
);

// ── Types ─────────────────────────────────────────────────────────────────────

type Kind = "header" | "offer" | "hero" | "destination";
type MediaType = "image" | "video";

interface CtaButton { label: string; icon?: string; action?: string; }
interface BlockData {
  size?: "wide" | "normal";
  imagePosition?: "left" | "bottom";
  buttons?: CtaButton[];
  cities?: string[];
  tags?: string[];
  [k: string]: unknown;
}
interface Block {
  id: number;
  kind: Kind;
  title: string | null;
  subtitle: string | null;
  body: string | null;
  mediaKey: string | null;
  mediaUrl: string | null;
  mediaType: MediaType | null;
  data: BlockData;
  sortOrder: number;
  isActive: boolean;
}
interface Article {
  id: number;
  title: string;
  imageKey: string | null;
  imageUrl: string | null;
  linkUrl: string | null;
  sortOrder: number;
  isActive: boolean;
}

// ── Static config ──────────────────────────────────────────────────────────────

const KIND_META: Record<Kind, { label: string; blurb: string; accent: string }> = {
  header:      { label: "Section Header",  blurb: "Title + subtitle at the top of the section", accent: "bg-slate-100 text-slate-700 border-slate-200" },
  offer:       { label: "Offer Card",      blurb: "Promo card with an image and short copy",     accent: "bg-amber-50 text-amber-700 border-amber-200" },
  hero:        { label: "Hero Banner",     blurb: "Full-width banner with heading + CTA buttons", accent: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  destination: { label: "Destination",     blurb: "City selector + media, description & tags",    accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const ICON_OPTIONS = [
  { value: "", label: "No icon" },
  { value: "flight", label: "Flight" },
  { value: "hotel", label: "Hotel" },
  { value: "visa", label: "Visa" },
];

const inputCls =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition";

// ── Small shared UI ─────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
      {label}{hint && <span className="normal-case font-normal text-gray-400"> — {hint}</span>}
    </label>
    {children}
  </div>
);

const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; labelOn?: string; labelOff?: string }> = ({
  on, onChange, labelOn = "Active", labelOff = "Hidden",
}) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border transition
      ${on ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
  >
    {on ? <FaEye size={11} /> : <FaEyeSlash size={11} />}
    {on ? labelOn : labelOff}
  </button>
);

// ── Media uploader (image / video) with live preview ────────────────────────────

const MediaUploader: React.FC<{
  mediaUrl: string | null;
  mediaType: MediaType | null;
  accept: "image" | "both";
  onUploaded: (m: { key: string; publicUrl: string; mediaType: MediaType }) => void;
  onClear: () => void;
}> = ({ mediaUrl, mediaType, accept, onUploaded, onClear }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Instant local preview from the picked file (a blob: URL). This renders
  // immediately and does NOT depend on the S3 object being publicly readable,
  // so the preview works even before the bucket policy is applied.
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [localIsVideo, setLocalIsVideo] = useState(false);

  // Revoke the blob URL when it changes or the component unmounts.
  useEffect(() => () => { if (localPreview) URL.revokeObjectURL(localPreview); }, [localPreview]);

  const acceptAttr = accept === "image" ? "image/*" : "image/*,video/*";

  const handleFile = async (file: File) => {
    setErr(null);
    const isVideo = file.type.startsWith("video/");
    if (accept === "image" && isVideo) { setErr("This slot only accepts images."); return; }
    // Show the local file right away.
    const objUrl = URL.createObjectURL(file);
    setLocalPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return objUrl; });
    setLocalIsVideo(isVideo);
    setUploading(true);
    setProgress(`Uploading ${(file.size / (1024 * 1024)).toFixed(1)} MB…`);
    try {
      const m = await uploadContentMedia(file);
      onUploaded(m);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const handleClear = () => {
    if (localPreview) { URL.revokeObjectURL(localPreview); setLocalPreview(null); }
    onClear();
  };

  // Prefer the just-picked local file; fall back to the stored URL (edit mode).
  const shownUrl = localPreview ?? mediaUrl;
  const shownIsVideo = localPreview ? localIsVideo : mediaType === "video";

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed overflow-hidden transition
          ${dragOver ? "border-primary bg-primary/5" : "border-gray-300 hover:border-primary/60 bg-gray-50"}`}
      >
        {shownUrl ? (
          <div className="relative group">
            {shownIsVideo ? (
              <video src={shownUrl} muted loop playsInline className="w-full h-44 object-cover" />
            ) : (
              <img src={shownUrl} alt="preview" className="w-full h-44 object-cover" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <span className="text-white text-xs font-semibold flex items-center gap-1">
                <FaCloudUploadAlt /> Replace
              </span>
            </div>
            <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-black/60 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
              {shownIsVideo ? <FaVideo size={9} /> : <FaImage size={9} />} {shownIsVideo ? "video" : "image"}
            </span>
          </div>
        ) : (
          <div className="h-44 flex flex-col items-center justify-center text-gray-400 gap-2 p-4 text-center">
            {uploading ? <FaSpinner className="animate-spin text-2xl text-primary" /> : <FaCloudUploadAlt className="text-3xl" />}
            <span className="text-xs font-medium">
              {uploading ? progress : `Drop ${accept === "image" ? "an image" : "an image or video"} here, or click to browse`}
            </span>
            <span className="text-[10px] text-gray-400">Images ≤ 10 MB · Videos ≤ 50 MB</span>
          </div>
        )}
        {uploading && shownUrl && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <FaSpinner className="animate-spin text-2xl text-primary" />
          </div>
        )}
      </div>

      <input ref={inputRef} type="file" accept={acceptAttr} hidden
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} />

      <div className="flex items-center justify-between mt-1.5 min-h-[18px]">
        {err ? <span className="text-[11px] text-red-500">{err}</span> : <span />}
        {shownUrl && !uploading && (
          <button type="button" onClick={handleClear} className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-1">
            <FaTrash size={9} /> Remove
          </button>
        )}
      </div>
    </div>
  );
};

// ── List editors for JSONB arrays (cities / tags) ───────────────────────────────

const StringListEditor: React.FC<{ items: string[]; onChange: (v: string[]) => void; placeholder: string }> = ({
  items, onChange, placeholder,
}) => (
  <div className="space-y-2">
    {items.map((val, i) => (
      <div key={i} className="flex items-center gap-2">
        <input
          className={inputCls}
          value={val}
          placeholder={placeholder}
          onChange={(e) => { const next = [...items]; next[i] = e.target.value; onChange(next); }}
        />
        <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
          className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:bg-red-50 transition">
          <FaTimes size={12} />
        </button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...items, ""])}
      className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
      <FaPlus size={9} /> Add
    </button>
  </div>
);

const ButtonListEditor: React.FC<{ items: CtaButton[]; onChange: (v: CtaButton[]) => void }> = ({ items, onChange }) => (
  <div className="space-y-2">
    {items.map((btn, i) => (
      <div key={i} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-gray-50 rounded-lg p-2 border border-gray-100">
        <input className={inputCls} value={btn.label} placeholder="Button label"
          onChange={(e) => { const n = [...items]; n[i] = { ...btn, label: e.target.value }; onChange(n); }} />
        <select className={`${inputCls} sm:w-32`} value={btn.icon ?? ""}
          onChange={(e) => { const n = [...items]; n[i] = { ...btn, icon: e.target.value }; onChange(n); }}>
          {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input className={`${inputCls} sm:w-44`} value={btn.action ?? ""} placeholder="scrollTop or /path"
          onChange={(e) => { const n = [...items]; n[i] = { ...btn, action: e.target.value }; onChange(n); }} />
        <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
          className="w-9 h-9 shrink-0 self-end flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:bg-red-50 transition">
          <FaTimes size={12} />
        </button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...items, { label: "", icon: "", action: "scrollTop" }])}
      className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
      <FaPlus size={9} /> Add button
    </button>
  </div>
);

// ── Block editor (adapts to kind) ────────────────────────────────────────────────

const BlockEditor: React.FC<{
  draft: Partial<Block>;
  onChange: (d: Partial<Block>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  saveLabel: string;
  lockKind?: boolean;
}> = ({ draft, onChange, onSave, onCancel, saving, saveLabel, lockKind }) => {
  const kind = (draft.kind ?? "offer") as Kind;
  const data = draft.data ?? {};
  const setData = (patch: Partial<BlockData>) => onChange({ ...draft, data: { ...data, ...patch } });

  const showMedia = kind !== "header";
  const mediaAccept: "image" | "both" = kind === "destination" ? "both" : "image";

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      className="bg-white border border-primary/30 rounded-xl p-5 space-y-4 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Field label="Block type">
          <select
            className={inputCls}
            value={kind}
            disabled={lockKind}
            onChange={(e) => onChange({ ...draft, kind: e.target.value as Kind })}
          >
            {(Object.keys(KIND_META) as Kind[]).map((k) => (
              <option key={k} value={k}>{KIND_META[k].label}</option>
            ))}
          </select>
        </Field>
        <p className="text-xs text-gray-400 flex-1 min-w-[180px] mt-4">{KIND_META[kind].blurb}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Field label={kind === "hero" || kind === "destination" ? "Heading" : "Title"}>
            <input className={inputCls} value={draft.title ?? ""} onChange={(e) => onChange({ ...draft, title: e.target.value })}
              placeholder="e.g. Build your travel with us." />
          </Field>

          {(kind === "header" || kind === "offer" || kind === "destination") && (
            <Field label="Subtitle">
              <input className={inputCls} value={draft.subtitle ?? ""} onChange={(e) => onChange({ ...draft, subtitle: e.target.value })}
                placeholder="Short supporting line" />
            </Field>
          )}

          {kind === "destination" && (
            <Field label="Description">
              <textarea className={`${inputCls} min-h-[90px]`} value={draft.body ?? ""}
                onChange={(e) => onChange({ ...draft, body: e.target.value })} placeholder="Paragraph shown next to the media" />
            </Field>
          )}

          {kind === "offer" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Width">
                <select className={inputCls} value={data.size ?? "normal"} onChange={(e) => setData({ size: e.target.value as any })}>
                  <option value="normal">Normal (1 column)</option>
                  <option value="wide">Wide (2 columns)</option>
                </select>
              </Field>
              <Field label="Image position">
                <select className={inputCls} value={data.imagePosition ?? "bottom"} onChange={(e) => setData({ imagePosition: e.target.value as any })}>
                  <option value="left">Beside text</option>
                  <option value="bottom">Below text</option>
                </select>
              </Field>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {showMedia && (
            <Field label={mediaAccept === "both" ? "Media (image or video)" : "Image"}>
              <MediaUploader
                mediaUrl={draft.mediaUrl ?? null}
                mediaType={draft.mediaType ?? null}
                accept={mediaAccept}
                onUploaded={(m) => onChange({ ...draft, mediaUrl: m.publicUrl, mediaKey: m.key, mediaType: m.mediaType })}
                onClear={() => onChange({ ...draft, mediaUrl: null, mediaKey: null, mediaType: null })}
              />
            </Field>
          )}
        </div>
      </div>

      {kind === "destination" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Cities" hint="the selector buttons">
            <StringListEditor items={data.cities ?? []} onChange={(v) => setData({ cities: v })} placeholder="City name" />
          </Field>
          <Field label="Attraction tags">
            <StringListEditor items={data.tags ?? []} onChange={(v) => setData({ tags: v })} placeholder="e.g. Sagrada Familia" />
          </Field>
        </div>
      )}

      {(kind === "hero" || kind === "destination") && (
        <Field label="CTA buttons">
          <ButtonListEditor items={data.buttons ?? []} onChange={(v) => setData({ buttons: v })} />
        </Field>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <Toggle on={draft.isActive !== false} onChange={(v) => onChange({ ...draft, isActive: v })} />
        <div className="flex gap-2">
          <button onClick={onCancel} disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            <FaTimes className="inline mr-1" size={11} /> Cancel
          </button>
          <button onClick={onSave} disabled={saving}
            className="px-5 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
            {saving ? <FaSpinner className="inline mr-1 animate-spin" size={11} /> : <FaSave className="inline mr-1" size={11} />}
            {saving ? "Saving…" : saveLabel}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ── Collapsed row summary for a block ────────────────────────────────────────────

const BlockRow: React.FC<{ block: Block; onEdit: () => void; onDelete: () => void; onToggle: () => void }> = ({
  block, onEdit, onDelete, onToggle,
}) => {
  const meta = KIND_META[block.kind];
  return (
    <div className={`flex items-center gap-3 p-3 ${block.isActive ? "" : "opacity-60"}`}>
      <span className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none">
        <FaGripVertical />
      </span>

      <div className="w-16 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
        {block.mediaUrl ? (
          block.mediaType === "video"
            ? <video src={block.mediaUrl} muted className="w-full h-full object-cover" />
            : <img src={block.mediaUrl} alt="" className="w-full h-full object-cover" />
        ) : <FaImage className="text-gray-300 text-xl" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.accent}`}>{meta.label}</span>
          {!block.isActive && <span className="text-[10px] text-gray-400">hidden</span>}
        </div>
        <p className="text-sm font-semibold text-gray-800 truncate">{block.title || <span className="italic text-gray-400">Untitled</span>}</p>
        {block.subtitle && <p className="text-xs text-gray-400 truncate">{block.subtitle}</p>}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <button onClick={onToggle} title={block.isActive ? "Hide" : "Show"}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition">
          {block.isActive ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
        </button>
        <button onClick={onEdit} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-primary transition">
          <FaPencilAlt size={11} />
        </button>
        <button onClick={onDelete} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-500 transition">
          <FaTrash size={11} />
        </button>
      </div>
    </div>
  );
};

// ── Explore blocks manager ───────────────────────────────────────────────────────

const EMPTY_BLOCK: Partial<Block> = { kind: "offer", title: "", data: {}, isActive: true };

const BlockManager: React.FC<{ onError: (e: string | null) => void }> = ({ onError }) => {
    const confirm = useConfirm();
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<Block>>({});
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<Partial<Block>>(EMPTY_BLOCK);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getContentBlocks();
      setBlocks(res.blocks ?? []);
      onError(null);
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || "Failed to load blocks");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  // Persist a new drag order (debounced so a drag doesn't spam the API).
  const persistOrder = (ordered: Block[]) => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      reorderContent("blocks", ordered.map((b, i) => ({ id: b.id, sortOrder: i * 10 })))
        .catch((e) => onError(e?.response?.data?.error || "Failed to save order"));
    }, 700);
  };

  const handleReorder = (ordered: Block[]) => { setBlocks(ordered); persistOrder(ordered); };

  const saveEdit = async () => {
    if (editingId == null) return;
    setSaving(true);
    try {
      await updateContentBlock(editingId, draft);
      setEditingId(null); setDraft({}); await load();
    } catch (e: any) { onError(e?.response?.data?.error || "Failed to save block"); }
    finally { setSaving(false); }
  };

  const saveCreate = async () => {
    setSaving(true);
    try {
      await createContentBlock({ ...createDraft, sortOrder: blocks.length * 10 });
      setCreating(false); setCreateDraft(EMPTY_BLOCK); await load();
    } catch (e: any) { onError(e?.response?.data?.error || "Failed to create block"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (b: Block) => {
    setBlocks((prev) => prev.map((x) => x.id === b.id ? { ...x, isActive: !x.isActive } : x));
    try { await updateContentBlock(b.id, { isActive: !b.isActive }); }
    catch (e: any) { onError(e?.response?.data?.error || "Failed to update"); load(); }
  };

  const remove = async (id: number) => {
    if (!(await confirm({
      title: "Delete this block?",
      message: "It will be removed from the page. This cannot be undone.",
      confirmLabel: "Delete block",
      tone: "danger",
    }))) return;
    try { await deleteContentBlock(id); await load(); }
    catch (e: any) { onError(e?.response?.data?.error || "Failed to delete"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500 hidden sm:block">Drag to reorder how blocks appear on the homepage.</p>
        <div className="flex items-center gap-2 ml-auto">
          <PreviewToggle on={showPreview} onClick={() => setShowPreview((v) => !v)} />
          {!creating && (
            <button onClick={() => { setCreating(true); setCreateDraft(EMPTY_BLOCK); setEditingId(null); }}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800 transition">
              <FaPlus size={11} /> Add block
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {creating && (
          <BlockEditor draft={createDraft} onChange={setCreateDraft} onSave={saveCreate}
            onCancel={() => setCreating(false)} saving={saving} saveLabel="Create block" />
        )}
      </AnimatePresence>

      {loading ? (
        <div className="text-center py-14 text-gray-400"><FaSpinner className="animate-spin inline mr-2" /> Loading…</div>
      ) : blocks.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl py-16 text-center">
          <FaLayerGroup className="text-gray-200 text-5xl mx-auto mb-3" />
          <p className="text-gray-500">No blocks yet</p>
        </div>
      ) : (
        <Reorder.Group axis="y" values={blocks} onReorder={handleReorder} className="space-y-3">
          {blocks.map((block) => (
            <Reorder.Item key={block.id} value={block}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden list-none">
              {editingId === block.id ? (
                <div className="p-1">
                  <BlockEditor draft={draft} onChange={setDraft} onSave={saveEdit}
                    onCancel={() => { setEditingId(null); setDraft({}); }} saving={saving} saveLabel="Save changes" lockKind />
                </div>
              ) : (
                <BlockRow block={block}
                  onEdit={() => { setEditingId(block.id); setDraft({ ...block }); setCreating(false); }}
                  onDelete={() => remove(block.id)}
                  onToggle={() => toggleActive(block)} />
              )}
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      {showPreview && (
        <PreviewFrame note="active blocks, exactly as visitors see them">
          <ExploreSection
            preview
            blocks={blocks.filter((b) => b.isActive).map((b): ExploreBlock => ({
              id: b.id, kind: b.kind, title: b.title, subtitle: b.subtitle, body: b.body,
              mediaUrl: b.mediaUrl, mediaType: b.mediaType, data: b.data as any,
            }))}
          />
        </PreviewFrame>
      )}
    </div>
  );
};

// ── Article manager ───────────────────────────────────────────────────────────────

const ArticleEditor: React.FC<{
  draft: Partial<Article>;
  onChange: (d: Partial<Article>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  saveLabel: string;
}> = ({ draft, onChange, onSave, onCancel, saving, saveLabel }) => (
  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
    className="bg-white border border-primary/30 rounded-xl p-5 space-y-4 shadow-sm">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-4">
        <Field label="Title">
          <textarea className={`${inputCls} min-h-[80px]`} value={draft.title ?? ""}
            onChange={(e) => onChange({ ...draft, title: e.target.value })}
            placeholder="Travelling to Turkey? Here's what you need." />
        </Field>
        <Field label="Link URL" hint="optional 'Read more' target">
          <input className={inputCls} value={draft.linkUrl ?? ""} onChange={(e) => onChange({ ...draft, linkUrl: e.target.value || null })}
            placeholder="https://…" />
        </Field>
      </div>
      <Field label="Cover image">
        <MediaUploader mediaUrl={draft.imageUrl ?? null} mediaType="image" accept="image"
          onUploaded={(m) => onChange({ ...draft, imageUrl: m.publicUrl, imageKey: m.key })}
          onClear={() => onChange({ ...draft, imageUrl: null, imageKey: null })} />
      </Field>
    </div>
    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
      <Toggle on={draft.isActive !== false} onChange={(v) => onChange({ ...draft, isActive: v })} />
      <div className="flex gap-2">
        <button onClick={onCancel} disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
          <FaTimes className="inline mr-1" size={11} /> Cancel
        </button>
        <button onClick={onSave} disabled={saving}
          className="px-5 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
          {saving ? <FaSpinner className="inline mr-1 animate-spin" size={11} /> : <FaSave className="inline mr-1" size={11} />}
          {saving ? "Saving…" : saveLabel}
        </button>
      </div>
    </div>
  </motion.div>
);

const EMPTY_ARTICLE: Partial<Article> = { title: "", isActive: true };

const ArticleManager: React.FC<{ onError: (e: string | null) => void }> = ({ onError }) => {
    const confirm = useConfirm();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<Article>>({});
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<Partial<Article>>(EMPTY_ARTICLE);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getContentArticles();
      setArticles(res.articles ?? []);
      onError(null);
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || "Failed to load articles");
    } finally { setLoading(false); }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const persistOrder = (ordered: Article[]) => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      reorderContent("articles", ordered.map((a, i) => ({ id: a.id, sortOrder: i * 10 })))
        .catch((e) => onError(e?.response?.data?.error || "Failed to save order"));
    }, 700);
  };
  const handleReorder = (ordered: Article[]) => { setArticles(ordered); persistOrder(ordered); };

  const saveEdit = async () => {
    if (editingId == null) return;
    setSaving(true);
    try { await updateContentArticle(editingId, draft); setEditingId(null); setDraft({}); await load(); }
    catch (e: any) { onError(e?.response?.data?.error || "Failed to save"); }
    finally { setSaving(false); }
  };
  const saveCreate = async () => {
    if (!createDraft.title?.trim()) { onError("Title is required"); return; }
    setSaving(true);
    try { await createContentArticle({ ...createDraft, sortOrder: articles.length * 10 }); setCreating(false); setCreateDraft(EMPTY_ARTICLE); await load(); }
    catch (e: any) { onError(e?.response?.data?.error || "Failed to create"); }
    finally { setSaving(false); }
  };
  const toggleActive = async (a: Article) => {
    setArticles((prev) => prev.map((x) => x.id === a.id ? { ...x, isActive: !x.isActive } : x));
    try { await updateContentArticle(a.id, { isActive: !a.isActive }); }
    catch (e: any) { onError(e?.response?.data?.error || "Failed to update"); load(); }
  };
  const remove = async (id: number) => {
    if (!(await confirm({
      title: "Delete this article?",
      message: "It will no longer appear on the site. This cannot be undone.",
      confirmLabel: "Delete article",
      tone: "danger",
    }))) return;
    try { await deleteContentArticle(id); await load(); }
    catch (e: any) { onError(e?.response?.data?.error || "Failed to delete"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500 hidden sm:block">Cards in the “Build memories” carousel. Drag to reorder.</p>
        <div className="flex items-center gap-2 ml-auto">
          <PreviewToggle on={showPreview} onClick={() => setShowPreview((v) => !v)} />
          {!creating && (
            <button onClick={() => { setCreating(true); setCreateDraft(EMPTY_ARTICLE); setEditingId(null); }}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800 transition">
              <FaPlus size={11} /> Add article
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {creating && (
          <ArticleEditor draft={createDraft} onChange={setCreateDraft} onSave={saveCreate}
            onCancel={() => setCreating(false)} saving={saving} saveLabel="Create article" />
        )}
      </AnimatePresence>

      {loading ? (
        <div className="text-center py-14 text-gray-400"><FaSpinner className="animate-spin inline mr-2" /> Loading…</div>
      ) : articles.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl py-16 text-center">
          <FaNewspaper className="text-gray-200 text-5xl mx-auto mb-3" />
          <p className="text-gray-500">No articles yet</p>
        </div>
      ) : (
        <Reorder.Group axis="y" values={articles} onReorder={handleReorder} className="space-y-3">
          {articles.map((a) => (
            <Reorder.Item key={a.id} value={a} className="bg-white border border-gray-200 rounded-xl overflow-hidden list-none">
              {editingId === a.id ? (
                <div className="p-1">
                  <ArticleEditor draft={draft} onChange={setDraft} onSave={saveEdit}
                    onCancel={() => { setEditingId(null); setDraft({}); }} saving={saving} saveLabel="Save changes" />
                </div>
              ) : (
                <div className={`flex items-center gap-3 p-3 ${a.isActive ? "" : "opacity-60"}`}>
                  <span className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none"><FaGripVertical /></span>
                  <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
                    {a.imageUrl ? <img src={a.imageUrl} alt="" className="w-full h-full object-cover" /> : <FaImage className="text-gray-300 text-xl" />}
                  </div>
                  <p className="flex-1 min-w-0 text-sm font-semibold text-gray-800 line-clamp-2">{a.title}</p>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => toggleActive(a)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition">
                      {a.isActive ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
                    </button>
                    <button onClick={() => { setEditingId(a.id); setDraft({ ...a }); setCreating(false); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-primary transition">
                      <FaPencilAlt size={11} />
                    </button>
                    <button onClick={() => remove(a.id)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-500 transition">
                      <FaTrash size={11} />
                    </button>
                  </div>
                </div>
              )}
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      {showPreview && (
        <PreviewFrame note="active articles, exactly as visitors see them">
          <ArticleCarousel
            preview
            articles={articles.filter((a) => a.isActive).map((a): CarouselArticle => ({
              id: a.id, title: a.title, imageUrl: a.imageUrl, linkUrl: a.linkUrl,
            }))}
          />
        </PreviewFrame>
      )}
    </div>
  );
};

// ── Top-level page ───────────────────────────────────────────────────────────────

const ContentManager: React.FC = () => {
  const [tab, setTab] = useState<"explore" | "articles">("explore");
  const [error, setError] = useState<string | null>(null);

  const TABS = [
    { id: "explore" as const, label: "Explore Section", icon: <FaLayerGroup /> },
    { id: "articles" as const, label: "Articles", icon: <FaNewspaper /> },
  ];

  return (
    <div className="max-w-screen-lg mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading font-sora flex items-center gap-2">
          <FaLayerGroup className="text-primary" /> Content Studio
        </h1>
        <p className="text-sm text-gray-500 mt-1">Manage the homepage Explore section and article carousel — text, images and videos.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); setError(null); }}
            className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition
              ${tab === t.id ? "text-primary" : "text-gray-500 hover:text-gray-700"}`}>
            {tab === t.id && (
              <motion.span layoutId="tabbg" className="absolute inset-0 bg-white rounded-lg shadow-sm" style={{ zIndex: 0 }} />
            )}
            <span className="relative z-10 flex items-center gap-2">{t.icon} {t.label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-5 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><FaTimes /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {tab === "explore" ? <BlockManager onError={setError} /> : <ArticleManager onError={setError} />}
    </div>
  );
};

export default ContentManager;
